import styled, { css, FlattenInterpolation, ThemeProps } from 'styled-components';
import { buttonResetStyleIncludingFocus } from '../../commonStyles/buttonReset';
import { twoLayeredBoxShadow } from '../../commonStyles/styling';
import colors from '../../helpers/colors';
import fonts from '../../helpers/fonts';
import mediaQuery from '../../helpers/mediaQuery';
import spacing from '../../helpers/spacing';
import { Theme } from '../../theme';
import Icon from '../Icon';
import { inlineTextLinkStyle } from '../Link/styles';

// We assume that line height is always 1.5em for MoreThan (it is, approximately), but this
// may not always be the case for other themes and may require overriding
const lineHeight = '1.5em';

const olBulletSize = '1em';
const ulBulletSize = '8px';

const desktopSpacingStyles = css`
  h3 {
    margin-top: ${spacing(4)};
  }

  ol,
  ul {
    padding-left: ${spacing(2)};

    > li {
      padding-left: ${spacing(2)};
    }
  }
`;

export type RichTextColor = (props: ThemeProps<Theme>) => string;

type RichTextWrapperProps = {
  useCustomBulletIcon: boolean;
  useOpenInNewTabIcons: boolean;
  useExternalOpenInNewTabIcons: boolean;
  backgroundColor?: RichTextColor;
  linkBorderColor?: RichTextColor;
};

const richTextInlineTextLinkStyle = ({
  backgroundColor,
  linkBorderColor,
}: RichTextWrapperProps): FlattenInterpolation<ThemeProps<Theme>> => css`
  ${inlineTextLinkStyle} {
  }
  ${(backgroundColor || linkBorderColor) &&
  css`
    &:focus-visible,
    &:active {
      box-shadow: ${twoLayeredBoxShadow(
        backgroundColor || colors.neutral08,
        linkBorderColor || colors.core02
      )};
    }
  `}
`;

export const unorderedListStyling = css`
  list-style: none;
  margin: ${spacing(2)} 0;
  padding-left: 0;
`;

export const unorderedListItemStylingWithCustomBulletIcon = css`
  margin: ${spacing(2)} 0;
  padding-left: ${spacing(4)};

  .bullet {
    position: relative;
    left: ${spacing(-2)};
    vertical-align: middle;
    margin-left: ${spacing(-2)}; /* match the icon width */
    line-height: 1;
  }
`;

export const unorderedListItemStyling = css`
  /* Spacing between the bullet and text is solely controlled by padding-left */
  padding-left: ${spacing(1.5)};
  /* Extra left margin for the bullet, allows padding to have full control of bullet-to-text spacing */
  margin: ${spacing(2)} 0 ${spacing(2)} ${ulBulletSize};
  position: relative;

  &::before {
    /* Style of bullet */
    border-radius: 50%;
    content: '';
    display: inline-block;
    height: ${ulBulletSize};
    width: ${ulBulletSize};
    background-color: ${colors.core01};

    /* Position of bullet */
    position: absolute;
    left: -${ulBulletSize};
    top: calc((${lineHeight} - ${ulBulletSize}) / 2);
  }
`;

export const DefaultStyledRichTextWrapper = styled.div<RichTextWrapperProps>`
  ${fonts.paragraph}
  ${({ backgroundColor }) =>
    backgroundColor &&
    css`
      background-color: ${backgroundColor};
    `};

  h1 {
    ${fonts.headingLarge}
    margin: ${spacing(6)} 0;
  }

  h2 {
    ${fonts.headingMedium}    
    margin: ${spacing(4)} 0 ${spacing(2)} 0;
  }

  h3 {
    ${fonts.headingSmall}
    margin: ${spacing(3)} 0 ${spacing(2)} 0;
  }

  h4 {
    ${fonts.headingXSmall}
    margin: ${spacing(2)} 0;
  }

  h5 {
    ${fonts.paragraphLarge};
    margin: ${spacing(2)} 0;
  }

  .heading-xlarge {
    ${fonts.headingXLarge}
    margin: ${spacing(4)} 0;
  }

  .heading-large {
    ${fonts.headingLarge}
    margin: ${spacing(3)} 0;
  }

  .heading-medium {
    ${fonts.headingMedium}
    margin: ${spacing(2)} 0;
  }

  .heading-small {
    ${fonts.headingSmall}
    margin: ${spacing(2)} 0;
  }

  .heading-xsmall {
    ${fonts.headingXSmall}
    margin: ${spacing(2)} 0;
  }

  /* For paragraphs, we only handle inter-paragraph spacing for simplicity, and we let the
  other elements next to it determine the spacing. */ 
  p {
    margin: 0;

    &.large {
      ${fonts.paragraphLarge}
    }

    &.small {
      ${fonts.paragraphSmall}
    }
  }

  p + p {
    margin-top: ${lineHeight};
  }

  /* Inter-paragraph spacing between paragraphs of different styles */
  p + p.large,
  p.large + p,
  p + p.small,
  p.small + p,
  p.small + p.large,
  p.large + p.small {
    margin-top: ${spacing(2)};
  }

  b,
  strong {
    font-weight: bold;
  }

  a {
    &:link {
      color: ${colors.core01};
    }
    ${richTextInlineTextLinkStyle}

    text-decoration: underline;

    span.open-in-new-tab {
      ${({ useOpenInNewTabIcons, useExternalOpenInNewTabIcons }) => {
        return useOpenInNewTabIcons || useExternalOpenInNewTabIcons
          ? css`
              margin-left: 0.4em;
              & > ${Icon} {
                height: 0.8em;
                width: 0.8em;
              }
            `
          : css`
              clip: rect(1px 1px 1px 1px); /* IE 6/7 */
              clip: rect(1px, 1px, 1px, 1px);
              height: 1px;
              overflow: hidden;
              position: absolute;
              white-space: nowrap;
              width: 1px;
            `;
      }}
    }
  }

  /**
  * Buttons should be styled as links - used for functional behaviour, e.g. modals.
  * The parent will be responsible for the onClick handler, using event bubbling.
  */
  button {
    ${buttonResetStyleIncludingFocus}
    /* default button font styles also need overriding, as we rely on inheritance here */
    line-height: inherit;
    font-size: inherit;
    font-weight: inherit;

    color: ${colors.core01};
    ${richTextInlineTextLinkStyle}
    text-decoration: underline;
    text-align: start;
  }

  ol {
    list-style: none;
    counter-reset: item;
    margin: ${spacing(2)} 0;
    padding-left: 0;

    > li {
      /* Spacing between the bullet and text is solely controlled by padding-left */
      padding-left: ${spacing(1.5)};
      counter-increment: item;
      /* Extra left margin for the bullet allows 
      padding to have full control of bullet-to-text spacing */
      margin: ${spacing(2)} 0 ${spacing(2)} ${olBulletSize};
      position: relative;

      &::before {
        /* Style of bullet */
        content: counter(item);
        font-size: ${olBulletSize};
        width: ${olBulletSize};
        height: ${olBulletSize};
        line-height: 1.115em;  /* manually determined to vertically center the number in the shaded circle */
        color: ${colors.neutral08};
        background: ${colors.core01};
        border-radius: 50%;
        text-align: center;
        display: inline-block;

        /* Position of bullet */
        position: absolute;
        left: -${olBulletSize};
        top: calc((${lineHeight} - ${olBulletSize}) / 2);
      }
    }
  }

  ul {
    ${unorderedListStyling}

    > li {
      ${(props) =>
        props.useCustomBulletIcon
          ? unorderedListItemStylingWithCustomBulletIcon
          : unorderedListItemStyling}

      &.large {
        ${fonts.paragraphLarge}
      }

      &.small {
        ${fonts.paragraphSmall}
      }
    }
  }

  table, th, td {
    border: 1px solid ${colors.neutral01};
    border-collapse: collapse;
  }

  table {
    width: 100%;
  }

  ${mediaQuery.desktop`${desktopSpacingStyles}`}
  
  /*  The bottom margin of the final element in the Rich Text component will
      not collapse as it is contained in the DefaultStyledRichTextWrapper div.
      Therefore, we need to set the bottom margin on the last element to 0.
      If the last element is a list element (ul/ol) then we also need to set 
      the final list item bottom margin to 0.
  */
  & > :last-child,
  & > :last-child > li:last-child {
    margin-bottom: 0;
  }
`;
